import React, { useState } from 'react';
import { useSlateStatic } from 'slate-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  icons,
  Radio,
} from '@ublend-npm/aulaui-next';

import {
  ItemsContainer,
  NumberOfItems,
  ModalDescription,
  CenterContent,
  OneLineContent,
  InlineLink,
} from './QuizItemsModal.styled';
import { useAulaEditorContext } from '../../EditorContext';
import { useFetchItems } from './quizHooks';
import { StatusTypes } from '../../lib/loadingStatusTypes';
import { Loading } from '../Loading/Loading';
import { ItemSelected } from '../../Entities';
import { quizType } from '../../plugins/quiz/quizType';

const goToItemBank = (institution: string, spaceId: string) => {
  const url = `https://${institution}.aula.education/#/dashboard/${spaceId}/journey/quizzes/item-bank`;
  window.open(url);
};

const TOOLTIP_TEXT = 'Item is already included';

export const QuizItemsModal: React.FunctionComponent<{
  entityId: string;
  onDeleteItem: () => void;
}> = ({ entityId, onDeleteItem }) => {
  const [open, setOpen] = useState(true);
  const { updateEntity, entities, config } = useAulaEditorContext();
  const editor = useSlateStatic();

  const { state, retry } = useFetchItems();
  const { data: items } = state;

  const [itemSelected, setItemSelected] = useState('');
  const chooseItem = (event: Event & { target: { value: string } }) => {
    const value = event?.target?.value;
    setItemSelected(value);
  };

  const confirmSelectedItem = () => {
    const item: ItemSelected = {
      reference: itemSelected,
    };

    updateEntity(entityId, {
      data: {
        item,
      },
    });
  };

  const onClose = () => {
    setOpen(false);
    onDeleteItem();
  };

  const loadingContent = (
    <CenterContent>
      <Loading />
      <div>Loading your items</div>
    </CenterContent>
  );

  const errorContent = (
    <OneLineContent>
      <icons.WarningIcon />
      <div>Can’t load items.</div>
      <Button type="text" onClick={retry}>
        Retry
      </Button>
    </OneLineContent>
  );

  const { institution, spaceId } = config;
  const goToItemBankLink = (e: React.MouseEvent) => {
    e.preventDefault();
    goToItemBank(institution, spaceId);
  };

  const noItems = (
    <CenterContent>
      <div>This bank is empty or does not contain multiple choice items.</div>
      <Button type="text" onClick={goToItemBankLink}>
        Go to item bank
      </Button>
    </CenterContent>
  );

  const existingQuizEntities = editor.children
    .filter(
      (child: any) =>
        child.type === quizType &&
        entities[child.entity] &&
        entities[child.entity].data.item,
    )
    .map((child: any) => entities[child.entity].data.item.reference);

  const itemsList = (
    <>
      <ModalDescription>
        Only multiple choice items are available.
        <br />
        You can manage items from your{' '}
        <InlineLink onClick={goToItemBankLink}>item bank</InlineLink>.
      </ModalDescription>
      <NumberOfItems>
        {items.length && <span>{`${items.length} items`}</span>}
      </NumberOfItems>
      <ItemsContainer>
        {items.map((item) => {
          const isUsedAlready = existingQuizEntities.includes(item.reference);
          const disabledProps = isUsedAlready
            ? {
                disabled: true,
                tooltipText: TOOLTIP_TEXT,
              }
            : {};

          return (
            <Radio
              name="items-modal"
              key={item.reference}
              value={item.reference}
              label={item.title}
              handleChange={chooseItem}
              isSelected={itemSelected === item.reference}
              ariaLabel={item.title}
              {...disabledProps}
            />
          );
        })}
      </ItemsContainer>
    </>
  );

  let content;
  switch (state.status) {
    case StatusTypes.LOADING:
    case StatusTypes.IDLE:
      content = loadingContent;
      break;
    case StatusTypes.FAILED:
      content = errorContent;
      break;
    case StatusTypes.SUCCESS:
      content = items.length === 0 ? noItems : itemsList;
      break;
    default:
      content = errorContent;
      break;
  }

  const primaryAction = {
    label: 'Select item',
    disabled: !itemSelected,
    onClick: confirmSelectedItem,
  };

  const secondaryAction = {
    label: 'Cancel',
    onClick: onClose,
  };

  return (
    <Dialog
      open={open}
      id="quiz-items-dialog"
      width="492"
      height="653"
      onClose={onClose}
    >
      <DialogTitle closeIcon={false}>
        Select an item from your item bank
      </DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions primary={primaryAction} secondary={secondaryAction} />
    </Dialog>
  );
};
